import axios from "axios";
import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useScreenClass } from "react-grid-system";
import MinimalSelect from "./MinimalSelect";
import VersionSelect from "./VersionSelect";
import { SemesterContext } from "../context/SemesterContext";
import { UniversityContext } from "../context/UniversityContext";
import { UserContext } from "../context/UserContext";
import { getDefaultExploreProgram } from "../utils/programName";
import FlexBox, { FlexGrow } from "./FlexBox";
import { EmptySemester } from "../types/Semester";

type ProgramSemester = {
  program_name: string;
  semester_id: string;
};

type MergedSemester = {
  merged_semester_name: string;
  program_semester: ProgramSemester[];
};

const ExploreMergedSelects = () => {
  const screenClass = useScreenClass();
  const navigate = useNavigate();
  const isMobile = useMemo(
    () => ["xs", "sm"].includes(screenClass),
    [screenClass]
  );
  const location = useLocation();

  const isExplore = location.pathname.includes("/explore");

  const semesterContext = useContext(SemesterContext);
  const universityContext = useContext(UniversityContext);
  const { user } = useContext(UserContext);

  const [mergedSemesters, setMergedSemesters] = useState<MergedSemester[]>([]);
  const [selectedMergedName, setSelectedMergedName] = useState("");
  const [mergedSelectsReady, setMergedSelectsReady] = useState(false);

  const resolveProgramName = (programName: string, choices: { program_name: string }[]) => {
    const match = choices.find((p) => p.program_name === programName);
    if (match) return match.program_name;
    return choices[0]?.program_name || programName;
  };

  const resolveProgram = (programName: string) => {
    const match = universityContext.programs?.find(
      (p: any) =>
        p.short_name === programName ||
        p.label === programName ||
        p.value === programName ||
        p.program_name === programName
    );
    if (match) return match;
    return {
      label: programName,
      value: programName,
      short_name: programName,
      program_name: programName,
      degree_abbrev: "",
      program_abbrev: "",
      id: "",
      description: "",
      first_semester: "",
      university_code: user?.university_code || "CMKL",
      versions: [],
    };
  };

  const applySelection = (
    mergedName: string,
    programName: string,
    list: MergedSemester[] = mergedSemesters
  ) => {
    const merged = list.find((s) => s.merged_semester_name === mergedName);
    if (!merged?.program_semester?.length) return false;

    const resolvedProgramName = resolveProgramName(
      programName,
      merged.program_semester
    );
    const entry = merged.program_semester.find(
      (p) => p.program_name === resolvedProgramName
    );
    if (!entry) return false;

    const year = mergedName.split(" ")[1] || "";
    setSelectedMergedName(mergedName);
    universityContext.setProgram(resolveProgram(resolvedProgramName));
    semesterContext.setSemester({
      label: mergedName,
      value: entry.semester_id,
      id: entry.semester_id,
      semester_name: mergedName,
      year,
      program_name: resolvedProgramName,
      is_open: false,
      add_drop_date: "",
      end_date: "",
      final_assess_date: "",
      instructor_grading_deadline: "",
      competency_recording_deadline: "",
      start_date: "",
      university_code: user?.university_code || "CMKL",
      university_name: "",
      term_ordinal: 0,
    });
    return true;
  };

  const selectedMerged = mergedSemesters.find(
    (s) => s.merged_semester_name === selectedMergedName
  );

  const programOptions = (selectedMerged?.program_semester || []).map((p) => ({
    label: p.program_name,
    value: p.program_name,
    semester_id: p.semester_id,
  }));

  const displayedSemesterName = selectedMergedName;

  useEffect(() => {
    setMergedSelectsReady(false);
    setSelectedMergedName("");

    semesterContext.setSemester(EmptySemester);

    axios
      .get(`/semester/merged`, {
        params: { university_code: user?.university_code || "CMKL" },
      })
      .then((res) => {
        const list: MergedSemester[] = res.data?.merged_semesters || [];
        setMergedSemesters(list);
        if (list.length === 0) {
          setMergedSelectsReady(true);
          return;
        }

        // Default semester for explore / instructor-competencies:
        const DEFAULT_MERGED_SEMESTER = "Fall 2026";
        const initialName =
          list.find((s) => s.merged_semester_name === DEFAULT_MERGED_SEMESTER)
            ?.merged_semester_name ||
          list[list.length - 1].merged_semester_name;
        // const initialName = list[list.length - 1].merged_semester_name;

        const programsForSemester = list.find(
          (s) => s.merged_semester_name === initialName
        )?.program_semester;
        if (!programsForSemester?.length) {
          setMergedSelectsReady(true);
          return;
        }

        const programChoices = programsForSemester.map((p) => ({
          label: p.program_name,
          value: p.program_name,
          short_name: p.program_name,
          program_name: p.program_name,
        }));
        const preferred =
          getDefaultExploreProgram(programChoices) || programChoices[0];
        applySelection(
          initialName,
          preferred.program_name || preferred.value,
          list
        );
        setMergedSelectsReady(true);
      })
      .catch((err) => {
        console.error("Failed to fetch merged semesters", err);
        setMergedSelectsReady(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.university_code]);

  // Re-apply when university programs load so we get full program ids
  useEffect(() => {
    if (!mergedSelectsReady || !selectedMergedName || !mergedSemesters.length) {
      return;
    }
    if (!universityContext.programs?.length) return;
    const currentName =
      universityContext.selectedProgram?.short_name ||
      universityContext.selectedProgram?.label;
    if (!currentName) return;
    const merged = mergedSemesters.find(
      (s) => s.merged_semester_name === selectedMergedName
    );
    const inList = merged?.program_semester?.some(
      (p) => p.program_name === currentName
    );
    if (inList) {
      applySelection(selectedMergedName, currentName);
    }
  }, [universityContext.programs, mergedSelectsReady]);

  const semesterOptions = mergedSemesters.map((s) => ({
    label: s.merged_semester_name,
    value: s.merged_semester_name,
  }));

  const selectedProgramOption =
    programOptions.find(
      (p) =>
        p.value === universityContext.selectedProgram?.short_name ||
        p.value === universityContext.selectedProgram?.label ||
        p.value === universityContext.selectedProgram?.value
    ) || programOptions[0];

  return (
    <FlexBox style={{ display: "flex", gap: 10 }}>
      <FlexGrow style={{ margin: 0 }}>
        <MinimalSelect
          setClass={isMobile ? "rs-container" : ""}
          placeholder="Select Semester"
          options={semesterOptions}
          value={
            semesterOptions.find((s) => s.value === displayedSemesterName) ??
            null
          }
          onChange={(data: any) => {
            const nextName = data.value;
            const programs =
              mergedSemesters.find((s) => s.merged_semester_name === nextName)
                ?.program_semester || [];
            if (!programs.length) return;
            const programChoices = programs.map((p) => ({
              label: p.program_name,
              value: p.program_name,
              short_name: p.program_name,
              program_name: p.program_name,
            }));
            const preferred =
              getDefaultExploreProgram(programChoices) || programChoices[0];
            applySelection(nextName, preferred.program_name || preferred.value);
          }}
        />
      </FlexGrow>
      <FlexGrow style={{ margin: 0 }}>
        {programOptions.length > 0 ? (
          <MinimalSelect
            setClass={isMobile ? "rs-container" : ""}
            placeholder="Program"
            options={programOptions}
            value={selectedProgramOption}
            onChange={(data: any) => {
              applySelection(selectedMergedName, data.value);
            }}
          />
        ) : (
          <div
            style={{
              color: "#c9c9c9",
              fontSize: 12,
              fontStyle: "italic",
              whiteSpace: "nowrap",
              padding: "6px 8px",
            }}
          >
            No programs for the selected semester.
          </div>
        )}
      </FlexGrow>
      {isExplore && (
        <FlexGrow style={{ margin: 0 }}>
          <VersionSelect
            onChange={(v: any) => {
              semesterContext.setSelectedSemesterVersion(v.value);
              navigate("/explore");
            }}
          />
        </FlexGrow>
      )}
    </FlexBox>
  );
};

export default ExploreMergedSelects;
